import { useState } from "react";

import { useI18n } from "../i18n/index.ts";
import type { GeoPoint } from "../lib/types.ts";

export interface LocateButtonProps {
  readonly onLocated: (point: GeoPoint) => void;
  readonly onError: (message: string) => void;
}

/**
 * Géolocalisation à la demande, jamais au chargement : la position n'est
 * demandée que si l'utilisateur clique, et n'est jamais envoyée au serveur.
 */
export const LocateButton = (props: LocateButtonProps): React.ReactElement => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  const locate = (): void => {
    if (!("geolocation" in navigator)) {
      props.onError(t.locate.unsupported);
      return;
    }

    setBusy(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setBusy(false);
        props.onLocated({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        setBusy(false);
        props.onError(t.locate.denied);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30_000 },
    );
  };

  return (
    <button
      type="button"
      className="button button--inline"
      onClick={locate}
      disabled={busy}
      aria-label={t.locate.label}
      title={t.locate.label}
    >
      {/*
        Un SVG et non le caractère « ◎ » : ses métriques de police donnaient au
        bouton une boîte de ligne de 36 px, qui le rendait plus haut que le champ
        voisin — d'où un décalage visible des bords arrondis. Un tracé a des
        dimensions prévisibles, et rend pareil sur toutes les plateformes.
      */}
      <svg
        viewBox="0 0 20 20"
        width="16"
        height="16"
        aria-hidden="true"
        style={busy ? { opacity: 0.45 } : undefined}
      >
        <circle cx="10" cy="10" r="5.4" fill="none" stroke="currentColor" strokeWidth="1.9" />
        <circle cx="10" cy="10" r="1.6" fill="currentColor" />
        <path
          d="M10 1.2v2.8M10 16v2.8M1.2 10h2.8M16 10h2.8"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
};
